import React, { useMemo, useCallback } from 'react';
import { Tree, ConfigProvider } from 'antd';
import type { TreeProps as AntTreeProps, TreeDataNode } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand } from '../../base/actionParser';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

/**
 * Tree Semantic DOM 样式配置
 * 参考 Ant Design 6.x Semantic DOM 规范
 */
export interface TreeSemanticStyles {
  root?: React.CSSProperties;
  item?: React.CSSProperties;
  itemIcon?: React.CSSProperties;
  itemTitle?: React.CSSProperties;
}

export interface TreeSemanticClassNames {
  root?: string;
  item?: string;
  itemIcon?: string;
  itemTitle?: string;
}

/**
 * Tree Design Token
 * 参考 Ant Design 6.x Design Token 规范
 */
export interface TreeToken {
  // 目录树
  directoryNodeSelectedBg?: string;
  directoryNodeSelectedColor?: string;
  // 缩进
  indentSize?: number;
  // 节点悬浮
  nodeHoverBg?: string;
  nodeHoverColor?: string;
  // 节点选中
  nodeSelectedBg?: string;
  nodeSelectedColor?: string;
  // 标题
  titleHeight?: number;
}

/** 与 Ant Design Tree 事件对应的 command 动作名，用于 Extend 中 createWatchCommands 注册 */
export interface AndTreeActionProps {
  selectAction?: string;
  checkAction?: string;
  expandAction?: string;
  dragEndAction?: string;
  dragEnterAction?: string;
  dragLeaveAction?: string;
  dragOverAction?: string;
  dragStartAction?: string;
  dropAction?: string;
  rightClickAction?: string;
  loadAction?: string;
}

export interface AndTreeProps
  extends Omit<
    AntTreeProps,
    | 'onSelect'
    | 'onCheck'
    | 'onExpand'
    | 'onDragEnd'
    | 'onDragEnter'
    | 'onDragLeave'
    | 'onDragOver'
    | 'onDragStart'
    | 'onDrop'
    | 'onRightClick'
    | 'onLoad'
    | 'expandAction'
  >,
  BaseComponentProps,
  DataSetConfig,
  AndTreeActionProps {
  onSelect?: (selectedKeys: React.Key[], info: any) => void;
  onCheck?: (checkedKeys: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }, info: any) => void;
  onExpand?: (expandedKeys: React.Key[], info: any) => void;
  onDragEnd?: (info: { event: React.DragEvent; node: any }) => void;
  onDragEnter?: (info: { event: React.DragEvent; node: any; expandedKeys: React.Key[] }) => void;
  onDragLeave?: (info: { event: React.DragEvent; node: any }) => void;
  onDragOver?: (info: { event: React.DragEvent; node: any }) => void;
  onDragStart?: (info: { event: React.DragEvent; node: any }) => void;
  onDrop?: (info: { event: React.MouseEvent; node: any; dragNode: any; dragNodesKeys: React.Key[] }) => void;
  onRightClick?: (info: { event: React.MouseEvent; node: any }) => void;
  onLoad?: (loadedKeys: React.Key[], info: { event: any; node: any }) => void;

  // Semantic DOM 支持
  styles?: TreeSemanticStyles;
  classNames?: TreeSemanticClassNames;

  // Design Token 支持
  token?: TreeToken;
}

export const AndTree: React.FC<AndTreeProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  selectAction,
  checkAction,
  expandAction,
  dragEndAction,
  dragEnterAction,
  dragLeaveAction,
  dragOverAction,
  dragStartAction,
  dropAction,
  rightClickAction,
  loadAction,
  onSelect,
  onCheck,
  onExpand,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDragStart,
  onDrop,
  onRightClick,
  onLoad,
  treeData: propTreeData,
  styles,
  classNames,
  token,
  ...restProps
}) => {
  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );
  const keySelected = useMemo(
    () => (dataSet ? getDataSetStoreKey(sessionId, `${dataSet}_selected`) : undefined),
    [sessionId, dataSet]
  );
  const keyChecked = useMemo(
    () => (dataSet ? getDataSetStoreKey(sessionId, `${dataSet}_checked`) : undefined),
    [sessionId, dataSet]
  );
  const keyExpanded = useMemo(
    () => (dataSet ? getDataSetStoreKey(sessionId, `${dataSet}_expanded`) : undefined),
    [sessionId, dataSet]
  );

  const storeTreeData = useAndStore((s) => {
    if (!dataSetKey) return undefined;
    if (dataSetField) {
      return s.getDataSet(dataSetKey, dataSetField) as TreeDataNode[] | undefined;
    }
    const root = s.dataSets[dataSetKey];
    if (!root) return undefined;
    if (Array.isArray(root)) return root as TreeDataNode[];
    const val = (root as { value?: TreeDataNode[] }).value;
    return Array.isArray(val) ? val : undefined;
  });
  const storeSelectedKeys = useAndStore((s) =>
    keySelected ? (s.getDataSet(keySelected, '') as React.Key[] | undefined) : undefined
  );
  const storeCheckedKeys = useAndStore((s) =>
    keyChecked ? (s.getDataSet(keyChecked, '') as React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] } | undefined) : undefined
  );
  const storeExpandedKeys = useAndStore((s) =>
    keyExpanded ? (s.getDataSet(keyExpanded, '') as React.Key[] | undefined) : undefined
  );

  // 设计器模式(develop)：优先 store，无则回退到静态 treeData 便于画布预览；运行时且配置了 dataSet：仅用 store，不回退到静态 treeData
  const treeData = useMemo(() => {
    if (develop) return storeTreeData ?? propTreeData ?? [];
    if (dataSetKey) return storeTreeData ?? [];
    return propTreeData ?? [];
  }, [develop, dataSetKey, storeTreeData, propTreeData]);
  const selectedKeys = storeSelectedKeys;
  const checkedKeys = storeCheckedKeys;
  const expandedKeys = storeExpandedKeys;

  const handleSelect = useCallback(
    (selectedKeysArr: React.Key[], info: any) => {
      if (onSelect) {
        onSelect(selectedKeysArr, info);
        return;
      }
      if (keySelected) setDataSet(keySelected, '', selectedKeysArr);
      if (selectAction && sessionId) {
        addCommand(sessionId, createCommand(selectAction, { selectedKeys: selectedKeysArr }));
      }
    },
    [onSelect, keySelected, sessionId, setDataSet, selectAction, addCommand]
  );

  const handleCheck = useCallback(
    (checkedKeysArr: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }, info: any) => {
      if (onCheck) {
        onCheck(checkedKeysArr, info);
        return;
      }
      if (keyChecked) setDataSet(keyChecked, '', checkedKeysArr);
      if (checkAction && sessionId) {
        addCommand(sessionId, createCommand(checkAction, { checkedKeys: checkedKeysArr }));
      }
    },
    [onCheck, keyChecked, sessionId, setDataSet, checkAction, addCommand]
  );

  const handleExpand = useCallback(
    (expandedKeysArr: React.Key[], info: any) => {
      if (onExpand) {
        onExpand(expandedKeysArr, info);
        return;
      }
      if (keyExpanded) setDataSet(keyExpanded, '', expandedKeysArr);
      if (expandAction && sessionId) {
        addCommand(sessionId, createCommand(expandAction, { expandedKeys: expandedKeysArr, expanded: info.expanded }));
      }
    },
    [onExpand, keyExpanded, sessionId, setDataSet, expandAction, addCommand]
  );

  const handleDragEnd = useCallback(
    (info: { event: React.DragEvent; node: any }) => {
      if (onDragEnd) {
        onDragEnd(info);
        return;
      }
      if (dragEndAction && sessionId) {
        addCommand(sessionId, createCommand(dragEndAction, info));
      }
    },
    [onDragEnd, dragEndAction, sessionId, addCommand]
  );

  const handleDragEnter = useCallback(
    (info: { event: React.DragEvent; node: any; expandedKeys: React.Key[] }) => {
      if (onDragEnter) {
        onDragEnter(info);
        return;
      }
      if (dragEnterAction && sessionId) {
        addCommand(sessionId, createCommand(dragEnterAction, info));
      }
    },
    [onDragEnter, dragEnterAction, sessionId, addCommand]
  );

  const handleDragLeave = useCallback(
    (info: { event: React.DragEvent; node: any }) => {
      if (onDragLeave) {
        onDragLeave(info);
        return;
      }
      if (dragLeaveAction && sessionId) {
        addCommand(sessionId, createCommand(dragLeaveAction, info));
      }
    },
    [onDragLeave, dragLeaveAction, sessionId, addCommand]
  );

  const handleDragOver = useCallback(
    (info: { event: React.DragEvent; node: any }) => {
      if (onDragOver) {
        onDragOver(info);
        return;
      }
      if (dragOverAction && sessionId) {
        addCommand(sessionId, createCommand(dragOverAction, info));
      }
    },
    [onDragOver, dragOverAction, sessionId, addCommand]
  );

  const handleDragStart = useCallback(
    (info: { event: React.DragEvent; node: any }) => {
      if (onDragStart) {
        onDragStart(info);
        return;
      }
      if (dragStartAction && sessionId) {
        addCommand(sessionId, createCommand(dragStartAction, info));
      }
    },
    [onDragStart, dragStartAction, sessionId, addCommand]
  );

  const handleDrop = useCallback(
    (info: { event: React.MouseEvent; node: any; dragNode: any; dragNodesKeys: React.Key[]; dropPosition?: number; dropToGap?: boolean }) => {
      if (onDrop) {
        onDrop(info);
        return;
      }
      if (dropAction && sessionId) {
        addCommand(sessionId, createCommand(dropAction, info));
      }
    },
    [onDrop, dropAction, sessionId, addCommand]
  );

  const handleRightClick = useCallback(
    (info: { event: React.MouseEvent; node: any }) => {
      if (onRightClick) {
        onRightClick(info);
        return;
      }
      if (rightClickAction && sessionId) {
        addCommand(sessionId, createCommand(rightClickAction, info));
      }
    },
    [onRightClick, rightClickAction, sessionId, addCommand]
  );

  const handleLoad = useCallback(
    (loadedKeys: React.Key[], info: { event: any; node: any }) => {
      if (onLoad) {
        onLoad(loadedKeys, info);
        return;
      }
      if (loadAction && sessionId) {
        addCommand(sessionId, createCommand(loadAction, { loadedKeys, ...info }));
      }
    },
    [onLoad, loadAction, sessionId, addCommand]
  );

  // 过滤空值的 token
  const filteredToken = useMemo(() => {
    if (!token) return undefined;
    const result: Record<string, any> = {};
    Object.entries(token).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null) {
        result[key] = value;
      }
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }, [token]);

  // 构建 theme 配置
  const themeConfig = useMemo(() => {
    const config: any = {};
    
    // develop 模式下禁用动画，避免 GrapesJS iframe 中的 CSSMotion 错误
    if (develop) {
      config.motion = false;
    }
    
    // 添加 token 配置
    if (filteredToken) {
      config.components = { Tree: filteredToken };
    }
    
    return Object.keys(config).length > 0 ? config : undefined;
  }, [develop, filteredToken]);

  // develop 模式下的 motion 配置
  // 必须同时在 Tree 组件上设置 motion 属性才能完全禁用展开动画
  const motionConfig = develop ? { motion: false } : {};

  const treeElement = (
    <Tree
      {...restProps}
      {...motionConfig}
      treeData={treeData}
      {...(keySelected != null ? { selectedKeys: selectedKeys ?? [] } : {})}
      {...(keyChecked != null ? { checkedKeys: checkedKeys ?? [] } : {})}
      {...(keyExpanded != null ? { expandedKeys: expandedKeys ?? [] } : {})}
      onSelect={handleSelect}
      onCheck={handleCheck}
      onExpand={handleExpand}
      onDragEnd={handleDragEnd}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
      onDrop={handleDrop}
      onRightClick={handleRightClick}
      onLoad={handleLoad}
      styles={styles}
      classNames={classNames}
    />
  );

  // 如果有 theme 配置，用 ConfigProvider 包裹
  if (themeConfig) {
    return (
      <ConfigProvider theme={themeConfig}>
        {treeElement}
      </ConfigProvider>
    );
  }

  return treeElement;
};

export default AndTree;
